import { useMemo, useRef, useState } from 'react';

import { GetLocationByIdResponseDTO, RecordState, type UpdateLocationRequestDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleF, GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { InputActionMeta } from 'react-select';
import { z } from 'zod';

import { AsyncSelect } from '@/components/ui/async-select';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { usePermissions } from '@/hooks/use-permissions';
import { getLocationByIdQueryOptions, locationKeys } from '@/queries/locations';
import { ApiService } from '@/services/api-v2';

import { LocationCreatableMultiSelectField, locationFormSchema, LocationInputField } from '../../../components/locations/location-form';

export default function LocationPage() {
  const { id } = useParams<{ id: string }>();
  const { canManageLocations } = usePermissions();

  const { data: location, isLoading } = useQuery({
    ...getLocationByIdQueryOptions({ id }),
    retry: false,
  });

  if (isLoading || !location) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="border-default-grey-2 size-[30px] animate-spin rounded-full border-4" />
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <div className="flex w-full flex-col gap-4 overflow-y-auto rounded-lg bg-white p-6 shadow-lg">
        <h1 className="text-xl font-medium">{location ? location.name : 'Location'}</h1>

        {location && <LocationForm location={location} locationId={id!} readOnly={!canManageLocations} />}
      </div>
    </div>
  );
}

function LocationForm({
  location,
  locationId,
  readOnly = false,
}: {
  location: GetLocationByIdResponseDTO['location'];
  locationId: string;
  readOnly?: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mapRef = useRef<GoogleMap>(null);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: env.VITE_GOOGLE_MAPS_API_KEY || '',
    // The workspace payload carries no country, so the Maps region biases to AU.
    region: 'au',
  });

  const addressToken = useMemo(() => crypto.randomUUID(), []);

  const getNumericValue = (value: string | number | undefined, defaultValue: number): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  };

  const form = useForm<z.infer<typeof locationFormSchema>>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: location?.name || '',
      status: location?.state === RecordState.Active,
      address: location?.address || undefined,
      placeId: location?.placeId || undefined,
      lat: location?.lat ? getNumericValue(location.lat, -26) : undefined,
      lng: location?.lng ? getNumericValue(location.lng, 135) : undefined,
      radius: location?.radius ? getNumericValue(location.radius, 15) : undefined,
      zoom: location?.zoom ? getNumericValue(location.zoom, 4) : undefined,
      emails: location?.emails ? location.emails.split(',').map((email) => ({ label: email, value: email })) : [],
    },
  });

  const address = form.watch('address');
  const lat = form.watch('lat');
  const lng = form.watch('lng');
  const radius = form.watch('radius');
  const zoom = form.watch('zoom');

  const { mutateAsync: fetchAddressDetails } = useMutation({
    mutationKey: ['address-details'],
    mutationFn: async ({ value, token }: { value: string; token: string }) => await (await ApiService.GetAddress({ id: value, token })).json(),
    gcTime: 0,
  });

  const { mutateAsync: updateLocation, isPending: isUpdating } = useMutation({
    mutationFn: async (values: z.infer<typeof locationFormSchema>) => {
      const currentZoom = mapRef.current?.state?.map?.getZoom() || values.zoom;

      const updateData: UpdateLocationRequestDTO = {
        name: values.name,
        status: values.status,
        emails: values.emails.map((email) => email.value),
        address: values.address,
        placeId: values.placeId,
        radius: values.radius,
        zoom: currentZoom,
        lat: values.lat,
        lng: values.lng,
      };

      return await (await ApiService.UpdateLocation(locationId, updateData)).json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: locationKeys.all,
      });
      toast.success('Location updated successfully');
    },
    onError: (error) => {
      toast.error(`Error updating location: ${error.message}`);
    },
  });

  const handleSubmit = async (data: z.infer<typeof locationFormSchema>) => {
    await updateLocation(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="gap-6">
          <div className="space-y-4">
            <LocationInputField label="Name" name="name" control={form.control} disabled={isUpdating || readOnly} />

            <Controller
              control={form.control}
              name="address"
              render={({ field: { value, onChange } }) => (
                <div className="flex flex-col gap-1">
                  <Label>Address</Label>
                  <LocationAddressSearch
                    token={addressToken}
                    value={value ? { label: value, value, secondary: '' } : undefined}
                    disabled={isUpdating || readOnly}
                    onChange={async (selectedValue) => {
                      onChange(selectedValue.label);
                      const result = await fetchAddressDetails({
                        value: selectedValue.value,
                        token: addressToken,
                      });
                      form.setValue('placeId', selectedValue.value);
                      form.setValue('lat', result.address?.geometry?.lat ?? 0);
                      form.setValue('lng', result.address?.geometry?.lng ?? 0);
                      form.setValue('address', [selectedValue.label, result.address?.city, result.address?.state].join(', '));
                      form.setValue('zoom', 20);
                    }}
                  />
                </div>
              )}
            />

            <div className="h-[400px] w-full">
              {isLoaded && (
                <GoogleMap
                  ref={mapRef}
                  mapContainerStyle={{ height: '100%', width: '100%' }}
                  center={{ lat: lat ?? -26, lng: lng ?? 135 }}
                  zoom={zoom}
                  options={{
                    mapTypeControl: true,
                    mapTypeControlOptions: {
                      style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                      position: google.maps.ControlPosition.TOP_RIGHT,
                    },
                    draggableCursor: 'crosshair',
                    zoomControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                  }}
                  onClick={(e: google.maps.MapMouseEvent) => {
                    if (!e.latLng || isUpdating || readOnly) return;
                    form.setValue('lat', e.latLng.lat());
                    form.setValue('lng', e.latLng.lng());
                  }}>
                  {!!address && !!lat && !!lng && (
                    <MarkerF
                      position={{
                        lat: lat,
                        lng: lng,
                      }}
                    />
                  )}
                  {!!lat && !!lng && !!radius && (
                    <CircleF
                      center={{
                        lat: lat,
                        lng: lng,
                      }}
                      options={{
                        strokeColor: '#2E81DA',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#2E81DA',
                        fillOpacity: 0.35,
                        clickable: false,
                        draggable: false,
                        editable: false,
                        visible: true,
                        radius: radius,
                        zIndex: 1,
                      }}
                    />
                  )}
                </GoogleMap>
              )}
            </div>

            <Controller
              control={form.control}
              name="radius"
              render={({ field: { value, onChange } }) => (
                <div className="flex flex-col gap-2">
                  <Label>Radius: {value}m</Label>
                  <Slider
                    min={10}
                    max={300}
                    value={value ? [value] : [15]}
                    onValueChange={(newValue) => onChange(newValue[0])}
                    disabled={isUpdating || readOnly}
                  />
                </div>
              )}
            />

            <LocationCreatableMultiSelectField label="Contact emails" name="emails" control={form.control} defaultOptions={[]} disabled={readOnly} />
          </div>
        </div>

        {!readOnly && (
          <div className="flex">
            <Button type="submit" variant="success" disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}

const debouncedSearchAddress = debounce(
  (
    inputValue: string,
    token: string,
    callback: (
      error: Error | null,
      result: Array<{
        label: string;
        secondary: string;
        value: string;
      }>,
    ) => void,
  ) => {
    ApiService.SearchAddresses({ query: inputValue, token })
      .then(async (response) => await response.json())
      .then((data) => {
        const options = data.addresses.map((result) => ({
          label: result.address.main_text,
          secondary: result.address.secondary_text,
          value: result.id,
        }));
        callback(null, options);
      })
      .catch((error: Error) => callback(error, []));
  },
  300,
);

function LocationAddressSearch({
  value,
  onChange,
  disabled,
  token,
}: {
  value?: { label: string; value: string; secondary: string };
  onChange?: (address: { label: string; value: string; secondary: string }) => void;
  disabled?: boolean;
  token: string;
}) {
  const [inputValue, setInputValue] = useState('');
  const [menuIsOpened, setMenuIsOpened] = useState(false);

  const loadOptions = (inputValue: string, callback: (options: Array<{ label: string; value: string; secondary: string }>) => void) => {
    if (!inputValue) {
      return callback([]);
    }
    debouncedSearchAddress(inputValue, token, (error, results) => {
      if (error) {
        callback([]);
        return;
      }
      callback(results.map(({ label, value, secondary }) => ({ label, value, secondary })));
    });
  };

  const handleMenuOpen = () => {
    setMenuIsOpened(true);
    setInputValue(value?.label || '');
  };

  const displayValueProps = menuIsOpened ? { inputValue } : { value: { label: value?.label || '', value: value?.value || '', secondary: '' } };

  return (
    <AsyncSelect<{ label: string; value: string; secondary: string }>
      isDisabled={disabled}
      loadOptions={loadOptions}
      onChange={(newValue) => {
        if (newValue) {
          onChange?.(newValue);
          setInputValue(newValue.label);
        }
      }}
      onInputChange={(value, actionMeta: InputActionMeta) => {
        if (actionMeta.action !== 'input-blur' && actionMeta.action !== 'menu-close') {
          setInputValue(value);
        }
      }}
      formatOptionLabel={({ label, secondary }: { label: string; secondary?: string }) => (
        <div>
          <div>{label}</div>
          <div className="text-[#b9b9c3]">{secondary}</div>
        </div>
      )}
      {...displayValueProps}
      onMenuClose={() => setMenuIsOpened(false)}
      onMenuOpen={handleMenuOpen}
      placeholder="Search for an address..."
    />
  );
}
